import { Skeleton } from "@/components/ui/skeleton";
import { useI18n } from "@/i18n";

type ResourceStateProps = {
  status: "idle" | "loading" | "success" | "empty" | "error";
  error?: Error | null;
  onRetry?: () => void;
  emptyMessage?: string;
  children: React.ReactNode;
};

export function ResourceState({
  status,
  error,
  onRetry,
  emptyMessage,
  children,
}: ResourceStateProps) {
  const { t } = useI18n();

  if (status === "loading" || status === "idle") {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full rounded-2xl" />
        <Skeleton className="h-48 w-full rounded-2xl" />
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center">
        <p className="font-display text-lg font-semibold">{t.admin.common.somethingWrong}</p>
        <p className="mt-2 text-sm text-muted-foreground">{error?.message}</p>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="mt-6 inline-flex h-9 items-center rounded-full bg-primary px-4 text-xs font-semibold text-primary-foreground"
          >
            {t.admin.common.retry}
          </button>
        ) : null}
      </div>
    );
  }

  if (status === "empty") {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center">
        <p className="text-sm text-muted-foreground">{emptyMessage ?? t.admin.common.noData}</p>
      </div>
    );
  }

  return <>{children}</>;
}
